import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";

const Symbol = () => {
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [symbolNameFilter, setSymbolNameFilter] = useState("");
  const [instrumentTypeFilter, setInstrumentTypeFilter] = useState("");
  const [exchangeFilter, setExchangeFilter] = useState("");
  const [instrumentTokenFilter, setInstrumentTokenFilter] = useState("");

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  const fetchSymbols = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}symbol`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          limit,
          page,
          symbol_name: symbolNameFilter,
          exchange: exchangeFilter,
          instrument_type: instrumentTypeFilter,
          instrument_token: instrumentTokenFilter,
        },
      });

      const data = response.data;
      setSymbols(data.data);
      setPageCount(data.totalPages);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSymbols();
  }, [
    page,
    instrumentTokenFilter,
    instrumentTypeFilter,
    exchangeFilter,
    symbolNameFilter,
  ]);

  useEffect(() => {
    const handleClearFilters = () => {
      setSymbolNameFilter("");
      setInstrumentTypeFilter("");
      setExchangeFilter("");
      setInstrumentTokenFilter("");
    };

    window.addEventListener("clearSymbolsFiltersEvent", handleClearFilters);
    return () =>
      window.removeEventListener(
        "clearSymbolsFiltersEvent",
        handleClearFilters
      );
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div className="page-header-fixed page-sidebar-fixed fade in mt-5">
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="d-flex align-items-center justify-content-between">
          <div className="panel-heading border-0">
            <h4 className="panel-title">Manage Symbol</h4>
          </div>

          <div className="me-3">
            <span style={{ fontSize: "0.8rem" }} className="me-2">
              {start} to {end} of {total}
            </span>
            <button
              className="btn btn-default btn-sm"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            &nbsp;
            <button
              className="btn btn-default btn-sm"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === pageCount}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-condensed table-hover bg-white">
            <thead>
              <tr>
                <th className="text-center">ID</th>
                <th>Symbol Name</th>
                <th>Instrument Token</th>
                <th>Exchange</th>
                <th>Instrument Type</th>
                <th>Strike</th>
                <th>Expiry Date</th>
                <th>Lot Size</th>
                <th>Segment</th>
              </tr>

              <tr>
                <th></th>
                <th>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Symbol Name"
                    value={symbolNameFilter}
                    onChange={(e) => setSymbolNameFilter(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Instrument Token"
                    value={instrumentTokenFilter}
                    onChange={(e) => setInstrumentTokenFilter(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Exchange"
                    value={exchangeFilter}
                    onChange={(e) => setExchangeFilter(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Instrument Type"
                    value={instrumentTypeFilter}
                    onChange={(e) => setInstrumentTypeFilter(e.target.value)}
                  />
                </th>
                <th colSpan="4"></th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: 10 }).map((_, rowIndex) => (
                  <tr key={`skeleton-${rowIndex}`} className="skeleton-row">
                    {Array.from({ length: 9 }).map((_, colIndex) => (
                      <td key={colIndex}>
                        <div className="skeleton-box"></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : symbols.length > 0 ? (
                symbols.map((symbol, index) => (
                  <tr key={symbol.id}>
                    <td className="text-center">
                      {(page - 1) * limit + index + 1}
                    </td>
                    <td>{symbol.symbol_name || symbol.symbol}</td>
                    <td>{symbol.instrument_token}</td>
                    <td>{symbol.exchange}</td>
                    <td>{symbol.instrument_type}</td>
                    <td>{symbol.strike}</td>
                    <td>{symbol.expiry_date || "NA"}</td>
                    <td>{symbol.lot_size}</td>
                    <td>{symbol.segment}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    No symbols found matching your criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Symbol;
